import { useEffect, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';

import { Icon, type IconComponent } from '@/shared/components/Icon';
import { hasMoreSpecificNavigationMatch } from '@/shared/lib/navigation-match';
import { cn } from '@/shared/lib/cn';

export interface NavDropdownItem {
  label: string;
  to: string;
  icon: IconComponent;
}

export interface NavDropdownLink {
  icon?: IconComponent;
  label: string;
  to: string;
}

export interface NavDropdownGroup {
  icon?: IconComponent;
  label: string;
  children: NavDropdownLink[];
}

export type NavDropdownEntry = NavDropdownLink | NavDropdownGroup;

const isGroup = (entry: NavDropdownEntry): entry is NavDropdownGroup =>
  'children' in entry;

/** Panel min-width plus its offset, used to pick the side a flyout opens on */
const FLYOUT_WIDTH = 232;

const flattenEntries = (entries: NavDropdownEntry[]): NavDropdownLink[] =>
  entries.flatMap((entry) => (isGroup(entry) ? entry.children : [entry]));

interface NavDropdownProps {
  icon: IconComponent;
  label: string;
  items: NavDropdownEntry[];
  variant?: 'light' | 'dark';
  compact?: boolean;
}

export function NavDropdown({
  icon,
  label,
  items,
  variant = 'dark',
  compact = false,
}: NavDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [openGroupLabel, setOpenGroupLabel] = useState<string | null>(null);
  const [flyoutToLeft, setFlyoutToLeft] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const location = useLocation();
  const isDark = variant === 'dark';

  const destinations = flattenEntries(items).map((item) => item.to);
  const linkEnd = (to: string) =>
    hasMoreSpecificNavigationMatch(location.pathname, to, destinations);
  const isLinkActive = (link: NavDropdownLink) =>
    !linkEnd(link.to) &&
    (location.pathname === link.to ||
      location.pathname.startsWith(`${link.to}/`));

  const isActive = flattenEntries(items).some(isLinkActive);

  const openGroup = (groupLabel: string, anchor: HTMLElement) => {
    const { right } = anchor.getBoundingClientRect();
    setFlyoutToLeft(right + FLYOUT_WIDTH > window.innerWidth);
    setOpenGroupLabel(groupLabel);
  };

  const openMenu = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsOpen(true);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setOpenGroupLabel(null);
  };

  const scheduleClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = setTimeout(() => {
      closeMenu();
      closeTimerRef.current = null;
    }, 150);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    closeMenu();
  }, [location.pathname]);

  const panelClassName = cn(
    'animate-slide-down rounded-lg border py-1 shadow-lg',
    isDark ? 'border-surface-border bg-surface' : 'text-border bg-card',
  );

  const itemClassName = (active: boolean) =>
    cn(
      'mx-1 flex gap-2 rounded-md px-3 py-2.5 text-[15px] transition-colors',
      active
        ? 'bg-primary/15 text-primary'
        : isDark
          ? 'text-surface-muted hover:bg-surface-hover hover:text-white'
          : 'text-secondary hover:bg-muted hover:text-body',
    );

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={() => {
          setIsOpen((prev) => !prev);
        }}
        aria-expanded={isOpen}
        aria-haspopup="true"
        className={cn(
          'relative flex items-center gap-1 whitespace-nowrap rounded-lg py-2 font-medium transition-colors',
          compact ? 'px-2 text-sm 2xl:text-base' : 'px-3 text-base',
          isActive
            ? 'text-primary after:absolute after:-bottom-3 after:left-0 after:w-full after:rounded-none after:border-b-2 after:border-primary'
            : isDark
              ? 'text-surface-muted hover:bg-surface-hover hover:text-white'
              : 'text-secondary hover:bg-muted hover:text-body',
        )}
      >
        <Icon icon={icon} className="size-4" />
        {label}
        <svg
          className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full z-50 min-w-[220px] pt-1"
          onMouseEnter={openMenu}
          onMouseLeave={scheduleClose}
        >
          <div className={panelClassName}>
            {items.map((item) =>
              isGroup(item) ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={(event) => {
                    openMenu();
                    openGroup(item.label, event.currentTarget);
                  }}
                >
                  <button
                    type="button"
                    onClick={(event) => {
                      if (openGroupLabel === item.label) {
                        setOpenGroupLabel(null);
                        return;
                      }
                      openGroup(item.label, event.currentTarget);
                    }}
                    aria-expanded={openGroupLabel === item.label}
                    aria-haspopup="true"
                    className={cn(
                      itemClassName(item.children.some(isLinkActive)),
                      'w-full text-left',
                    )}
                  >
                    {item.icon && (
                      <Icon icon={item.icon} className="mt-1 size-4 shrink-0" />
                    )}
                    <span className="flex-1">{item.label}</span>
                    <ChevronRight className="mt-1 size-4 shrink-0" />
                  </button>

                  {openGroupLabel === item.label && (
                    <div
                      className={cn(
                        'absolute top-0 z-50 min-w-[220px]',
                        flyoutToLeft ? 'right-full pr-1' : 'left-full pl-1',
                      )}
                    >
                      <div className={panelClassName}>
                        {item.children.map((child) => (
                          <NavLink
                            key={child.to}
                            to={child.to}
                            end={linkEnd(child.to)}
                            onClick={closeMenu}
                            className={({ isActive: childActive }) =>
                              itemClassName(childActive)
                            }
                          >
                            {child.icon && (
                              <Icon
                                icon={child.icon}
                                className="mt-1 size-4 shrink-0"
                              />
                            )}
                            <span className="flex-1">{child.label}</span>
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={linkEnd(item.to)}
                  onClick={closeMenu}
                  onMouseEnter={() => {
                    setOpenGroupLabel(null);
                  }}
                  className={({ isActive: itemActive }) =>
                    itemClassName(itemActive)
                  }
                >
                  {item.icon && (
                    <Icon icon={item.icon} className="mt-1 size-4 shrink-0" />
                  )}
                  <span className="flex-1">{item.label}</span>
                </NavLink>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
}
